import {connect} from 'react-redux';

import {TriageActionsEnum} from '../../../../../../../srcCommon/inboxes/viewer/conversation/triageActionHelpers';
import {makeGetWillShowTriageForTeamAlert} from '../../../../../../../srcCommon/inboxes/viewer/conversation/triageActionSelectors';
import {
  ConversationStatusesEnum,
  makeFindConversationActionContext,
} from '../../../../../../helpers/models/conversationHelpers';
import {ResolveDispatchProps} from '../../../../../../helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
  wrapInShallowSelector,
} from '../../../../../../helpers/reselectHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {BulkActionsTrackingTypesEnum} from '../../../../../../services/tracking';
import {AppState} from '../../../../../../store/appState';
import {makeGetAreAllArchived} from '../../../../../../store/data/conversations/conversationsMultiSelectors';
import {makeGetConversationContextNamespaces} from '../../../../../../store/data/conversations/conversationsSelectors';
import {
  setBulkConversationsStatusAction,
  setConversationsStatusAction,
} from '../../../../../../store/data/conversations/statusConversationsActions';
import {getIsUserNewUser} from '../../../../../../store/data/teammates/teammatesSelectors';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../../../store/panels/panelStateBase';
import {ConversationsArchiveCoordinator} from './conversationsArchiveCoordinator';

/*
 * Props.
 */

interface ConversationsArchiveCoordinatorContainerProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  /** Potentially partial set of currently selected conversation. */
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
  onInterfaceEvent?: (action: BulkActionsTrackingTypesEnum, isBulk: boolean) => void;
}

/*
 * Selectors.
 */

/** Extract the cells context from the props. */
function getCellsContext(
  _state: AppState,
  props: Pick<ConversationsArchiveCoordinatorContainerProps, 'cellsContext'>,
) {
  return props.cellsContext;
}

function getConversations(_state: unknown, props: ConversationsArchiveCoordinatorContainerProps) {
  return props.conversations;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationsArchiveCoordinatorContainerProps>()(() => ({
  contextNamespaces: wrapInShallowSelector(
    makeGetConversationContextNamespaces(getCellsContext, getConversations),
  ),
  areAllArchived: makeGetAreAllArchived(getCellsContext, getConversations),
  shouldShowArchiveTeamAlert: makeGetWillShowTriageForTeamAlert(
    getCellsContext,
    getConversations,
    TriageActionsEnum.ARCHIVE,
  ),
  shouldShowNewUserTooltip: getIsUserNewUser,
}));

const mapDispatchToProps = makeMapDispatchToProps<ConversationsArchiveCoordinatorContainerProps>()(() => ({
  setConversationsStatus: setConversationsStatusAction,
  setBulkConversationsStatus: setBulkConversationsStatusAction,
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: ConversationsArchiveCoordinatorContainerProps,
) {
  const {cellsContext, inboxesParameters, conversations, onInterfaceEvent} = ownProps;
  // TODO: update this to be selection to support bulk actions
  const conversationIds = conversations.map((c) => c.id);

  const onArchive = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.ARCHIVE, false);
    }

    dispatchProps.setConversationsStatus(
      cellsContext,
      conversationIds,
      ConversationStatusesEnum.ARCHIVED,
      makeFindConversationActionContext(cellsContext, conversations),
    );
  };

  const onReopen = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.REOPEN, false);
    }

    dispatchProps.setConversationsStatus(
      cellsContext,
      conversationIds,
      ConversationStatusesEnum.OPEN,
      makeFindConversationActionContext(cellsContext, conversations),
    );
  };

  const onBulkArchive = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.ARCHIVE, true);
    }

    dispatchProps.setBulkConversationsStatus(
      inboxesParameters,
      cellsContext,
      ConversationStatusesEnum.ARCHIVED,
    );
  };

  const onBulkReopen = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.REOPEN, true);
    }

    dispatchProps.setBulkConversationsStatus(inboxesParameters, cellsContext, ConversationStatusesEnum.OPEN);
  };

  return {
    ...ownProps,
    ...stateProps,
    onArchive,
    onReopen,
    onBulkArchive,
    onBulkReopen,
  };
}

export const ConversationsArchiveCoordinatorContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(ConversationsArchiveCoordinator);
